import { useCallback, useRef } from 'react';
import type { LayoutChangeEvent, NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import {
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import { DURATION } from '@/constants/animation';

/**
 * Collapses the directory hero's headline and description once the reader
 * starts scrolling, giving the height back to the expert cards.
 *
 * Only the prose collapses. The search field and filters stay put, because they
 * are the point of the screen and scrolling is often how someone starts hunting
 * for the control they want. A header that takes the search box with it would
 * trade a row of cards for the reason the reader came.
 *
 * The thresholds differ on purpose: collapsing and expanding at the same offset
 * makes the header flap when a scroll settles near the boundary, so it takes a
 * deliberate return toward the top to bring the prose back.
 */
const COLLAPSE_AFTER = 24;

const EXPAND_BEFORE = 8;

/**
 * The band breathes like a hero while the prose is there and tightens to a
 * toolbar once only the controls remain, so the collapsed state does not keep
 * hero-sized padding around a single row.
 *
 * Expanded, the top is short of the bottom on purpose. The headline's line box
 * carries leading above the cap-height while the bottom edge is measured from an
 * input's box, which has none, so equal padding reads top-heavy. The correction
 * is dropped when collapsed, where the first element is a control like the last
 * one and even padding is what looks even.
 */
const EXPANDED_PADDING_TOP = 14;

const EXPANDED_PADDING_BOTTOM = 20;

const COLLAPSED_PADDING = 12;

export function useCollapsibleDirectoryIntro() {
  /**
   * Measured from a child that never resizes. Measuring the animated element
   * itself would feed its collapsing height back in as the new natural height,
   * and it would never expand again.
   */
  const naturalHeight = useSharedValue(0);

  const collapsed = useSharedValue(0);

  const prefersReducedMotion = useReducedMotion();

  const isCollapsed = useRef(false);

  const onIntroLayout = useCallback(
    (event: LayoutChangeEvent) => {
      naturalHeight.value = event.nativeEvent.layout.height;
    },
    [naturalHeight]
  );

  const introStyle = useAnimatedStyle(() => {
    // Before the first layout pass the natural height is unknown, so the intro
    // renders at its own size rather than collapsing to nothing.
    if (!naturalHeight.value) {
      return { overflow: 'hidden' };
    }

    return {
      height: naturalHeight.value * (1 - collapsed.value),
      opacity: 1 - collapsed.value,
      overflow: 'hidden',
    };
  });

  const bandStyle = useAnimatedStyle(() => {
    const ease = collapsed.value;

    return {
      paddingTop: EXPANDED_PADDING_TOP + (COLLAPSED_PADDING - EXPANDED_PADDING_TOP) * ease,
      paddingBottom: EXPANDED_PADDING_BOTTOM + (COLLAPSED_PADDING - EXPANDED_PADDING_BOTTOM) * ease,
    };
  });

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const offset = event.nativeEvent.contentOffset.y;

      const next = isCollapsed.current ? offset > EXPAND_BEFORE : offset > COLLAPSE_AFTER;

      if (next === isCollapsed.current) {
        return;
      }

      isCollapsed.current = next;

      const target = next ? 1 : 0;

      collapsed.value = prefersReducedMotion
        ? target
        : withTiming(target, { duration: DURATION.fast });
    },
    [collapsed, prefersReducedMotion]
  );

  return { onIntroLayout, introStyle, bandStyle, handleScroll };
}
